/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import styles from '@app/components/channel/embeds/NSFWBlurOverlay.module.css';
import {MediaNSFWGateDetailsModal} from '@app/components/modals/MediaNSFWGateDetailsModal';
import {Tooltip} from '@app/components/uikit/tooltip/Tooltip';
import {NSFWGateReason} from '@app/stores/GuildNSFWAgreeStore';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type {FC, MouseEvent} from 'react';
import {useCallback} from 'react';

interface NSFWBlurOverlayProps {
	reason: NSFWGateReason;
	canReveal?: boolean;
	onReveal?: () => void;
}

export const NSFWBlurOverlay: FC<NSFWBlurOverlayProps> = observer(({reason, canReveal = false, onReveal}) => {
	const {t} = useLingui();
	const isGateReason = reason === NSFWGateReason.GEO_RESTRICTED || reason === NSFWGateReason.AGE_RESTRICTED;

	const handleOpenDetails = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			if (!isGateReason) {
				return;
			}
			ModalActionCreators.push(modal(() => <MediaNSFWGateDetailsModal reason={reason} />));
		},
		[isGateReason, reason],
	);

	if (isGateReason) {
		return (
			<div className={styles.revealOverlay}>
				<Tooltip text={t`Click for details`} position="top">
					<button type="button" className={styles.revealButton} onClick={handleOpenDetails}>
						{t`Reveal`}
					</button>
				</Tooltip>
			</div>
		);
	}

	if (!canReveal || !onReveal) return null;

	return (
		<div className={styles.revealOverlay}>
			<Tooltip text={t`Click to reveal`} position="top">
				<button type="button" className={styles.revealButton} onClick={onReveal}>
					{t`Reveal`}
				</button>
			</Tooltip>
		</div>
	);
});
